import {type PropsWithChildren, createContext, useContext} from 'react';
import {useController} from 'react-hook-form';
import {FormErrorMessage} from './error-message';
import {FieldDescription} from './field-description';
import {
  useCompanyRegistrationFormContext,
  type FormFieldPath,
} from '@/features/company-registration/form';

type FormFieldContextType = FormFieldPath;

/**
 * name に対して単一の入力欄、説明（optional）、エラーメッセージを持つ
 * シンプルなフィールドを表示するための FormField を補助する context
 */
const FormFieldContext = createContext<FormFieldContextType>(
  'companyInfo.corporateName',
);

/**
 * FormField.Root 内のフィールド実装のヘルパー
 */
export const useFormField = (options?: {
  /** Form.Description があるかどうか; aria-describedby の値に影響 */
  hasDescription?: boolean;
}) => {
  const name = useContext(FormFieldContext);

  const {register, control} = useCompanyRegistrationFormContext();

  const {
    fieldState: {invalid},
  } = useController({control, name});

  const ariadescribedby =
    options?.hasDescription === true
      ? `${name}-error ${name}-description`
      : `${name}-error`;

  const fieldProps = {
    ...register(name),
    id: name,
    // FormField.Description、FormField.Error を参照する
    'aria-describedby': ariadescribedby,
    'aria-invalid': invalid,
    error: invalid,
  };

  return {
    fieldProps,
    isError: invalid,
    control,
  };
};

/**
 * name の provider
 */
function FormFieldRoot({
  name,
  children,
}: PropsWithChildren<{
  readonly name: FormFieldPath;
}>) {
  return (
    <FormFieldContext.Provider value={name}>
      {children}
    </FormFieldContext.Provider>
  );
}

/**
 * context から name を取得して ${name}-description という id で FieldDescription を表示する
 */
function FormFieldDescription({
  children,
  ...props
}: PropsWithChildren<{readonly className?: string}>) {
  const name = useContext(FormFieldContext);
  return (
    <FieldDescription id={`${name}-description`} {...props}>
      {children}
    </FieldDescription>
  );
}

/**
 * context から name を取得して FormErrorMessage に渡して表示する
 */
function FormFieldError() {
  const name = useContext(FormFieldContext);

  return <FormErrorMessage name={name} />;
}

/**
 * name に対して単一の入力欄、説明（optional）、エラーメッセージを持つ
 * シンプルなフィールドを表示するための FormField;
 * 数が多いため抽象化したが、複雑なフィールドでは必ずしも使用する必要はない
 */
export const FormField = {
  Root: FormFieldRoot,
  Description: FormFieldDescription,
  Error: FormFieldError,
};
